// DNS-over-HTTPS fallback resolution — docs/04-censorship-resistance.md §1, §2.
//
// Browsers cannot dial a WSS URL by a client-resolved IP (TLS hostname
// verification is not overridable from JS), so DoH here serves the two things
// a web client CAN do: (1) sanity-check that a failing endpoint is a DNS
// problem rather than a network problem, and (2) surface resolved IPs so
// ip-kind endpoints (with SPKI pins, native/Android path) can be raced.
// The resolver is transport-injected and fully unit-testable.
//
// Three outcomes, not two. "A resolver answered and our name has no address"
// and "no resolver answered at all" are the difference between *our domain is
// blocked* and *this link reaches nothing* — different diagnoses, different
// next moves — and a bare address list collapses them into the same `[]`.
//
// Fingerprint discipline (docs/04 §5): one ordinary HTTPS GET per query, the
// `accept` header the dns-json API requires and nothing else — no `X-Merkury-*`,
// no cache-buster, no parallel burst that would stand out from a browser
// following the same public resolver's documented API.

export interface DohAnswer {
  name: string;
  type: number;
  data: string;
}

export type FetchLike = (
  url: string,
  init?: { headers?: Record<string, string>; signal?: AbortSignal },
) => Promise<{ ok: boolean; json(): Promise<unknown> }>;

/** The HTTPS transport a DoH query rides. Structurally a `fetch`. */
export type DohFetch = FetchLike;

/** Timer source for the per-query deadline — the caller's `TimerHost`, so the
 *  deadline runs on the same clock as everything else it schedules. */
export interface DohTimers {
  setTimeout(fn: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
}

/**
 * Ceiling on ONE query. Every query gets its own, which is the whole point: a
 * null-routed resolver accepts the connection and then says nothing forever, and
 * the resolver most likely to be null-routed in the target country is
 * `cloudflare-dns.com` — `doh[0]` in the shipped list. One shared budget would
 * let that entry spend all of it and leave every fallback behind it untried,
 * i.e. delete the fallbacks in exactly the conditions they exist for.
 */
export const DOH_QUERY_TIMEOUT_MS = 2_500;

export interface DohOptions {
  /** Per-query ceiling; defaults to DOH_QUERY_TIMEOUT_MS. */
  queryTimeoutMs?: number;
  /** Timer source; defaults to the host's own `setTimeout` when it has one. */
  timers?: DohTimers;
  /** Checked before every query: `true` abandons the rest of the lookup (the
   *  caller stopped — sign-out — and no longer wants the answer). */
  cancelled?: () => boolean;
}

/** The bare JSContext the iOS brain runs in may have no timers at all; without
 *  one there is no deadline to arm, so the caller's own bound is the only one. */
const hostTimers: DohTimers | null =
  typeof setTimeout === 'function'
    ? {
        setTimeout: (fn, ms) => setTimeout(fn, ms),
        clearTimeout: (h) => clearTimeout(h as Parameters<typeof clearTimeout>[0]),
      }
    : null;

/**
 * What a DoH probe established.
 *
 *  - `resolved`   the name has addresses: whatever is failing is not DNS.
 *  - `unresolved` a resolver answered for this name and it has no address:
 *                 the NAME is what is broken (poisoned, withdrawn, blocked).
 *  - `unreachable` not one resolver answered: we learned nothing about DNS,
 *                 because the link (or the resolvers themselves) is dead.
 */
export type DohOutcome =
  | { kind: 'resolved'; addresses: string[] }
  | { kind: 'unresolved' }
  | { kind: 'unreachable' };

const A_RECORD = 1;
const AAAA_RECORD = 28;

/** dns-json `Status` values that are a real answer about this name (RFC 8484
 *  RCODEs): NOERROR and NXDOMAIN. SERVFAIL and friends say the resolver failed,
 *  not that the name is gone, so they hand over to the next resolver. */
const NOERROR = 0;
const NXDOMAIN = 3;

interface DnsJson {
  Status?: unknown;
  Answer?: DohAnswer[];
}

/**
 * One dns-json query, bounded. The deadline covers the request AND the body
 * read — a resolver that returns headers and then stalls the body is the same
 * mid-flight freeze (docs/04 §1) and must not hold the chain either. Returns
 * null when the resolver produced no readable DNS answer; throws on the
 * deadline, which the caller reads the same way: this one said nothing.
 */
async function queryOnce(
  url: string,
  fetchFn: DohFetch,
  timers: DohTimers | null,
  ms: number,
): Promise<DnsJson | null> {
  const controller =
    timers !== null && typeof AbortController !== 'undefined' ? new AbortController() : null;
  const init: { headers: Record<string, string>; signal?: AbortSignal } = {
    headers: { accept: 'application/dns-json' },
  };
  if (controller) init.signal = controller.signal;
  const request = (async (): Promise<DnsJson | null> => {
    const res = await fetchFn(url, init);
    if (!res.ok) return null;
    return (await res.json()) as DnsJson | null;
  })();
  if (timers === null) return request;
  // The deadline may settle the race first; the request's own late failure is
  // then nobody's business (and an unhandled rejection if left floating).
  request.catch(() => {});
  let timer: unknown = null;
  const deadline = new Promise<never>((_, reject) => {
    timer = timers.setTimeout(() => {
      // Cut the connection loose rather than merely stop waiting on it: at one
      // probe per cooldown, an abandoned-but-open request per resolver
      // saturates a WebView's per-host connection pool, and later probes then
      // queue without ever reaching the wire.
      controller?.abort();
      reject(new Error('doh query timeout'));
    }, ms);
  });
  try {
    return await Promise.race([request, deadline]);
  } finally {
    timers.clearTimeout(timer);
  }
}

/**
 * Resolve a hostname via the endpoint list's DoH servers, trying each in order,
 * and report which of the three things above happened. Returns as soon as any
 * server answers with an address; an empty resolver list is `unreachable`
 * without a single request. Every query is individually bounded, so the whole
 * lookup is bounded by `dohUrls.length × 2 × queryTimeoutMs` — and no single
 * resolver can consume the budget the ones behind it need.
 */
export async function lookupViaDoh(
  hostname: string,
  dohUrls: readonly string[],
  fetchFn: DohFetch,
  opts: DohOptions = {},
): Promise<DohOutcome> {
  const timers = opts.timers ?? hostTimers;
  const perQueryMs = opts.queryTimeoutMs ?? DOH_QUERY_TIMEOUT_MS;
  const queries: string[] = [];
  for (const base of dohUrls) {
    for (const type of ['A', 'AAAA'] as const) {
      queries.push(`${base}?name=${encodeURIComponent(hostname)}&type=${type}`);
    }
  }
  // Set by a response we could actually read AS DNS. A captive portal's 200 OK
  // full of HTML is not an answer about our name, and must not be read as one.
  let answered = false;
  for (const url of queries) {
    if (opts.cancelled?.() === true) break;
    try {
      const body = await queryOnce(url, fetchFn, timers, perQueryMs);
      if (body === null) continue;
      const answers = (body.Answer ?? [])
        .filter((a) => a.type === A_RECORD || a.type === AAAA_RECORD)
        .map((a) => a.data);
      if (answers.length > 0) return { kind: 'resolved', addresses: answers };
      if (body.Status === NOERROR || body.Status === NXDOMAIN) answered = true;
    } catch {
      // this resolver said nothing (dead, or past its deadline): try the next
    }
  }
  return answered ? { kind: 'unresolved' } : { kind: 'unreachable' };
}

/**
 * Addresses for a hostname, or [] when nothing resolved it. Kept as the plain
 * form for callers that only want addresses (the native paths, which can dial
 * one with a pinned cert); callers that must tell a dead name from a dead link
 * want `lookupViaDoh`.
 */
export async function resolveViaDoh(
  hostname: string,
  dohUrls: readonly string[],
  fetchFn: DohFetch,
  opts: DohOptions = {},
): Promise<string[]> {
  const outcome = await lookupViaDoh(hostname, dohUrls, fetchFn, opts);
  return outcome.kind === 'resolved' ? outcome.addresses : [];
}
